import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react';
import { magnifyValue, STOCK_UI } from '@diamond/shared/decimal-util-web';
import type { Cart } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  apiInstance,
  buyerCaptureException,
  buyerCurrencyFormatter,
  convertToFloat,
  getBannerReferer,
  getItemCardTrackingParams,
  isEven,
  numberWithSeparator,
  roundUpToMultiple,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import { selectCartItems, useStockNumberInput } from '@diamond/sol-buyer/utils';
import {
  CART_TOTAL_QUERY_KEY,
  useCartStore,
  useCartTotalProductDetail,
} from '@diamond/sol-buyer-context';
import { type QueryKey, QueryClientContext } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { dinero, toDecimal } from 'dinero.js';
import { useContext, useEffect, useMemo, useState } from 'react';

import { QuantityControl } from '../../carts/QuantityControl';
import { CardProductProps } from '../card-product/card-product';
import { ModalNotification } from '../modal-notification';

interface AddToCartPopupProps {
  isOpen: boolean;
  onClose: () => void;
  product?: CardProductProps['product'];
  productId: string;
  token: string;
  selectedShipToId: string;
  onSuccess?: () => void;
  section?: string;
  page?: string;
  defaultQuantity?: number;
  autoSelectProductId?: string;
  enableCartStoreSync?: boolean;
  shouldAutoSelectOnAdd?: boolean;
  onSyncStart?: () => void;
  onSyncComplete?: () => void;
}

export const AddToCartPopup: React.FC<AddToCartPopupProps> = ({
  isOpen,
  onClose,
  product,
  productId,
  token,
  selectedShipToId,
  onSuccess,
  section,
  page,
  defaultQuantity,
  autoSelectProductId,
  enableCartStoreSync = false,
  shouldAutoSelectOnAdd = false,
  onSyncStart,
  onSyncComplete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const [available, setAvailable] = useState<
    'Available' | 'Out of Stock' | 'Stok Terbatas'
  >('Out of Stock');
  const [statusColor, setStatusColor] = useState<'azure' | 'red' | 'orange'>(
    'red'
  );
  const toast = useToast();
  const queryClient = useContext(QueryClientContext);
  const invalidateQuery = (key: QueryKey) => {
    if (!queryClient) {
      return Promise.resolve();
    }

    return queryClient.invalidateQueries({
      queryKey: key,
      exact: false,
      refetchType: 'active',
    });
  };

  const [magnifiedQty, setMagnifiedQty] = useState<number>(0);
  const [uiQuantity, setUiQuantity] = useState<number>(0);

  // Convert step, min, max using dinero.js for precision handling
  const orderQtyMultiple = Number(
    toDecimal(
      dinero({
        amount: product?.order_quantity_multiple || 1,
        currency: STOCK_UI,
      })
    )
  );

  const minOrderQty = Number(
    toDecimal(
      dinero({
        amount: product?.minimum_order_quantity || 1,
        currency: STOCK_UI,
      })
    )
  );

  const maxStock = Number(
    toDecimal(
      dinero({
        amount: product?.stock ?? 0,
        currency: STOCK_UI,
      })
    )
  );

  // Calculate max stock based on even/odd multiples
  const maxStockGanjil =
    Math.floor(maxStock / orderQtyMultiple) * orderQtyMultiple;

  const maxStockGenap =
    Math.round(maxStock / orderQtyMultiple) * orderQtyMultiple;

  const maxAllowedQuantity =
    isEven(orderQtyMultiple) || orderQtyMultiple === 1
      ? maxStockGanjil
      : maxStockGenap;

  const precision = orderQtyMultiple !== 1 ? 2 : 0;

  const normalizedDefaultQuantity = useMemo(() => {
    if (
      typeof defaultQuantity !== 'number' ||
      Number.isNaN(defaultQuantity) ||
      defaultQuantity <= 0
    ) {
      return minOrderQty;
    }

    const maxTarget =
      maxAllowedQuantity && maxAllowedQuantity > 0
        ? maxAllowedQuantity
        : defaultQuantity;
    const clamped = Math.min(maxTarget, Math.max(defaultQuantity, minOrderQty));

    if (!orderQtyMultiple || orderQtyMultiple <= 0) {
      return clamped;
    }

    const multiples = Math.max(1, Math.round(clamped / orderQtyMultiple));
    const alignedValue = Number(
      (multiples * orderQtyMultiple).toFixed(precision)
    );

    return Math.min(alignedValue, maxTarget);
  }, [
    defaultQuantity,
    minOrderQty,
    maxAllowedQuantity,
    orderQtyMultiple,
    precision,
  ]);

  useEffect(() => {
    if (normalizedDefaultQuantity > 0 && magnifiedQty === 0) {
      setUiQuantity(normalizedDefaultQuantity);
    }
  }, [normalizedDefaultQuantity, magnifiedQty]);

  const { inputProps, incProps, decProps, hasFractionalStep } =
    useStockNumberInput({
      id: `input-qty-modal-${productId}`,
      name: `input-qty-modal-${productId}`,
      valueUi: uiQuantity || normalizedDefaultQuantity,
      stepUi: orderQtyMultiple,
      minUi: minOrderQty,
      maxUi: maxAllowedQuantity,
      stepMagnified: product?.order_quantity_multiple || 1,
      roundOnBlurOnly: true,
      isDisabled:
        minOrderQty > maxStock || product?.stock_status === 'OUT_OF_STOCK',
      onChangeMagnifiedImmediate: (magnified) => {
        setMagnifiedQty(magnified);
        setUiQuantity(magnified / 1000);
      },
      onChangeMagnified: (magnified) => {
        // useStockNumberInput already provides rounded value in onChangeMagnified after debounce/blur
        setMagnifiedQty(magnified);
        setUiQuantity(magnified / 1000);
      },
      onError: (errorMessage) => {
        showToast(toast, 'error', errorMessage, undefined, 'bottom', 3000);
      },
    });

  // Use current magnified quantity or default for price fetch
  const effectiveMagnifiedQty =
    magnifiedQty || normalizedDefaultQuantity * 1000;

  // Use cartTotalProductDetail hook to get price calculations from API
  const { data: cartTotalData, isFetching: isCartTotalFetching } =
    useCartTotalProductDetail(productId, effectiveMagnifiedQty, isOpen);

  // Calculate prices using API data
  const { totalPrice, isMagnifiedPriceDiff } = useMemo(() => {
    if (!cartTotalData || !product) {
      return {
        totalPrice: 0,
        originalPrice: 0,
        savedPrice: 0,
        isMagnifiedPriceDiff: false,
      };
    }

    const calculatedOriginalPrice =
      cartTotalData.originalPriceMagnify?.magnifiedValue || 0;
    const calculatedTotalPrice =
      cartTotalData.finalPriceMagnify?.magnifiedValue || 0;
    const calculatedSavedPrice = calculatedOriginalPrice - calculatedTotalPrice;

    return {
      totalPrice: Math.round(calculatedTotalPrice),
      originalPrice: Math.round(calculatedOriginalPrice),
      savedPrice: Math.round(calculatedSavedPrice),
      isMagnifiedPriceDiff: calculatedSavedPrice > 0,
    };
  }, [cartTotalData, product]);

  // Set availability status based on stock
  useEffect(() => {
    const stockStatus = product?.stock_status || '';
    const available =
      maxStock > 0 && stockStatus === 'AVAILABLE'
        ? 'Available'
        : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
        ? 'Stok Terbatas'
        : 'Out of Stock';

    const color =
      maxStock > 0 && stockStatus === 'AVAILABLE'
        ? 'azure'
        : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
        ? 'orange'
        : 'red';
    setAvailable(available);
    setStatusColor(color);
  }, [maxStock, product?.stock_status]);

  const handleAddToCart = async () => {
    setIsLoading(true);
    setError('');
    try {
      const finalMagnifiedQty =
        magnifiedQty || normalizedDefaultQuantity * 1000;
      const payload = {
        product_id: productId,
        warehouse_id: selectedShipToId,
        quantity: finalMagnifiedQty,
        note: null,
      };
      await apiInstance(token).post('/cart', payload);

      const bannerReferer = getBannerReferer();

      trackEvent(TRACKING_EVENTS.ADD_TO_CART, {
        currency: 'IDR',
        value: totalPrice,
        items: [
          {
            ...getItemCardTrackingParams(
              product,
              finalMagnifiedQty / 1000,
              section,
              page
            ),
            index: 1,
            item_list_id: `${section} | ${product?.item_code}`,
            item_list_name: section,
          },
        ],
        section_name: section || '-',
        page_name: page || '-',
        ...(bannerReferer || {}),
      });

      try {
        if (enableCartStoreSync) {
          // Signal sync start
          onSyncStart?.();

          // Invalidate Cart query to mark as stale and trigger refetch for active queries
          await queryClient?.invalidateQueries({
            queryKey: ['Cart'],
            exact: false,
            refetchType: 'active',
          });

          // Also explicitly refetch all Cart queries to ensure they're updated
          await queryClient?.refetchQueries({
            queryKey: ['Cart'],
            exact: false,
            type: 'all',
          });

          // Get updated cart data from cache after refetch
          // The Cart query key is ['Cart', selectedShipToId, warehouseId, searchQuery]
          // Try to get data from any matching Cart query
          const cartQueries = queryClient?.getQueriesData({
            queryKey: ['Cart'],
            exact: false,
          });

          let cartQueryData: Cart[] | undefined;
          if (cartQueries && cartQueries.length > 0) {
            // Find the query with data that contains our product
            for (const [, data] of cartQueries) {
              if (Array.isArray(data) && data.length > 0) {
                const foundItem = (data as Cart[]).find(
                  (item) => item.product.id === productId
                );
                if (foundItem) {
                  cartQueryData = data as Cart[];
                  break;
                }
              }
            }
            // If no match found, use first available data
            if (!cartQueryData) {
              for (const [, data] of cartQueries) {
                if (Array.isArray(data) && data.length > 0) {
                  cartQueryData = data as Cart[];
                  break;
                }
              }
            }
          }

          // Find the updated cart item by product ID
          let updatedCartItem: Cart | undefined;
          if (cartQueryData && Array.isArray(cartQueryData)) {
            updatedCartItem = cartQueryData.find(
              (item) => item.product.id === productId
            );
          }

          // Update cartStore with new quantity from API
          if (updatedCartItem) {
            updateCartItemByProductId(productId, updatedCartItem);

            // Auto-select item if requested (for Cart page context)
            if (shouldAutoSelectOnAdd && autoSelectProductId === productId) {
              const isAlreadySelected = selectedCartItemIds.includes(
                updatedCartItem.id
              );

              if (!isAlreadySelected) {
                const preparedItems = selectCartItems(
                  [updatedCartItem.id],
                  [updatedCartItem]
                );
                preparedItems.forEach((item) => addCartItem(item));
              }
            }

            // Directly update React Query cache to ensure UI re-renders
            // Update all Cart queries that match the pattern ['Cart', ...]
            // This ensures CartList component re-renders with updated quantity
            const cartQueriesToUpdate = queryClient?.getQueriesData({
              queryKey: ['Cart'],
              exact: false,
            });

            if (cartQueriesToUpdate && cartQueriesToUpdate.length > 0) {
              cartQueriesToUpdate.forEach(([queryKey, data]) => {
                if (Array.isArray(data)) {
                  const currentData = data as Cart[];
                  const itemIndex = currentData.findIndex(
                    (item) => item.product.id === productId
                  );

                  if (itemIndex !== -1) {
                    const oldItem = currentData[itemIndex];

                    // Update the specific cart item in the array
                    // Create a new array and new item object to ensure React detects the change
                    const updatedData = currentData.map((item, idx) =>
                      idx === itemIndex
                        ? { ...updatedCartItem } // Spread to create new object reference
                        : item
                    );
                    queryClient?.setQueryData(queryKey, updatedData);
                  }
                }
              });
            } else {
              console.warn('[AddToCartPopup] No Cart queries found to update');
            }
          } else {
            console.warn('[AddToCartPopup] updatedCartItem not found:', {
              productId,
              cartQueryDataLength: cartQueryData?.length,
            });
          }

          // Now invalidate cart total - it will use updated store data
          await invalidateQuery([CART_TOTAL_QUERY_KEY]);

          // Signal sync complete
          onSyncComplete?.();
        } else {
          // Original behavior - just invalidate queries
          await Promise.all([
            invalidateQuery(['Cart']),
            invalidateQuery([CART_TOTAL_QUERY_KEY]),
          ]);

          if (autoSelectProductId) {
            queueAutoSelectProductId(autoSelectProductId);
          }
        }

        setSuccess(true);
        if (onSuccess) onSuccess();
      } catch (invalidateError) {
        console.error('Cart update failed:', invalidateError);
        setError('Gagal memperbarui keranjang');
        buyerCaptureException(
          { message: 'Cart update failed' },
          (scope, user) => {
            scope.setTag('cart-invalidation', `productId ${productId}`);
            scope.setUser({
              ...user,
              additional: {
                selectedShipToId: selectedShipToId,
                section: section || '-',
                page: page || '-',
              },
            });
            scope.setLevel('error');
          }
        );
      }
    } catch (err) {
      const axiosError = err as AxiosError<{
        message?: string;
        error?: string;
      }>;
      const errorCode = axiosError?.response?.data?.error;
      const message =
        axiosError?.response?.data?.message || 'Gagal menambah ke keranjang';

      if (errorCode === 'DAILY_ORDER_LIMIT_EXCEEDED') {
        showToast(toast, 'error', message);
        handleClose();
      } else {
        setError(message);
      }

      buyerCaptureException(
        axiosError?.response?.data || { message },
        (scope, user) => {
          scope.setTag('add-to-cart', `productId ${productId}`);
          scope.setUser({
            ...user,
            additional: {
              selectedShipToId: selectedShipToId,
              section: section || '-',
              page: page || '-',
            },
          });
          scope.setLevel('error');
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError('');
    // setQuantity(defaultQty);
    onClose();
  };

  // Store product details in cart store when component mounts
  const {
    setProductDetails,
    queueAutoSelectProductId,
    updateCartItemByProductId,
    addCartItem,
    selectedCartItemIds,
  } = useCartStore((state) => ({
    setProductDetails: state.setProductDetails,
    queueAutoSelectProductId: state.queueAutoSelectProductId,
    updateCartItemByProductId: state.updateCartItemByProductId,
    addCartItem: state.add,
    selectedCartItemIds: state.cartItems,
  }));

  useEffect(() => {
    if (product) {
      // Store product details for local price calculation
      setProductDetails(productId, {
        basePrice: product?.prices?.[0]?.price || 0,
        discount_promos:
          product?.discount_promos?.map((promo) => ({
            id: promo.id,
            // Map discount promo properties to the format expected by the cart store
            // is_discount true means percentage discount, false means fixed amount
            type: promo.is_discount ? 'PERCENTAGE' : 'FIXED_AMOUNT',
            value: promo.deduction_amount,
          })) || [],
      });
    }
  }, [product, productId, setProductDetails]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size={isMobile ? 'xs' : 'md'}
    >
      <ModalOverlay />
      <ModalContent borderRadius="md">
        {!success ? (
          <ModalHeader fontSize="xl" fontWeight="bold">
            Atur Jumlah Pesanan
          </ModalHeader>
        ) : null}
        <ModalCloseButton size="lg" />
        <ModalBody>
          {success ? (
            <ModalNotification
              isOpen={success}
              onClose={handleClose}
              title={'Berhasil ditambahkan ke dalam keranjang!'}
              primaryLabel={'Lanjutkan Belanja'}
              onPrimary={handleClose}
              secondaryLabel={'Lihat Keranjang'}
              onSecondary={() => (window.location.href = '/cart')}
              size={isMobile ? 'xs' : 'md'}
            />
          ) : (
            <>
              <Flex align="center" mb={4} gap={4}>
                <Box
                  boxSize={20}
                  bg="gray.100"
                  rounded="md"
                  overflow="hidden"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {/* Product Image */}
                  {product?.product_image_thumbnails?.[0] && (
                    <img
                      src={product?.product_image_thumbnails?.[0]}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectPosition: 'center',
                        objectFit: 'contain',
                      }}
                      alt={product?.desc || 'Product image'}
                    />
                  )}
                </Box>
                <Box flex={1} minW={0}>
                  <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                    {product?.brand_desc}
                  </Text>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    {product?.desc}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {product?.item_code}
                  </Text>
                </Box>
              </Flex>
              <Box mb={3}>
                <QuantityControl
                  variant="add-to-cart"
                  hasFractionalStep={hasFractionalStep}
                  inputProps={inputProps}
                  incProps={incProps}
                  decProps={decProps}
                  isLoading={isCartTotalFetching || isLoading}
                />
              </Box>
              {defaultQuantity && defaultQuantity > 0 ? (
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Saran voucher: tambah{' '}
                  {numberWithSeparator(normalizedDefaultQuantity)} unit
                </Text>
              ) : null}
              <Text
                color={statusColor}
                fontWeight="semibold"
                textAlign="left"
                mb={2}
                fontSize="sm"
              >
                {available}
              </Text>
              <Box mb={3}>
                <Text fontSize="md" fontWeight="bold" mb={1}>
                  Total
                </Text>
                {isMagnifiedPriceDiff ? (
                  <Box mb="1">
                    <Text
                      as="span"
                      color="gray.75"
                      textDecoration="line-through"
                    >
                      {buyerCurrencyFormatter(
                        cartTotalData.originalPriceMagnify.magnifiedValue
                      )}
                    </Text>
                    <Text as="span" ml="2" color="#FF0000">
                      Hemat{' '}
                      {buyerCurrencyFormatter(
                        cartTotalData.savedPriceMagnify.magnifiedValue
                      )}
                    </Text>
                  </Box>
                ) : null}
                <Skeleton borderRadius="md" isLoaded={!isCartTotalFetching}>
                  <Box py="2" px="4" flex="1" bgColor="gray.100" rounded="lg">
                    <Text color="blue.500" fontWeight="bold" fontSize="xl">
                      {buyerCurrencyFormatter(
                        cartTotalData.finalPriceMagnify.magnifiedValue
                      )}
                    </Text>
                  </Box>
                </Skeleton>
              </Box>
              {error && (
                <Text color="red.500" fontSize="sm" mb={2} textAlign="center">
                  {error}
                </Text>
              )}
            </>
          )}
        </ModalBody>
        {!success && (
          <ModalFooter>
            <Button
              w="full"
              colorScheme="blue"
              onClick={handleAddToCart}
              isDisabled={
                isCartTotalFetching ||
                isLoading ||
                product?.stock_status === 'OUT_OF_STOCK' ||
                minOrderQty > maxStock
              }
              isLoading={isLoading || isCartTotalFetching}
              borderRadius="md"
              height="48px"
            >
              Tambah ke Keranjang
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
